import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * A recording attached to a comment plays in place (docs/designs/recordings-on-issues.md Phase 1, prototype
 * docs/prototypes/record-tool/flow.js togglePlayer). Playback loads on Play; the thumbnail separately decodes a small still. Saved bytes come through the surface's
 * own authorised attachment route, the blob is typed by the server's content type, and the URL is revoked when the
 * player closes or unmounts. A playback error shows the approved line and hands the row back to its download.
 *
 * Three parts so the pin popover (Play in the 40x30 thumb slot, the player as the next row) and the comment lists
 * (a labelled Play button beside the download) can lay them out differently: useVideoAttachment owns the state,
 * VideoPlayButton is the trigger, VideoAttachmentPlayer is the video or the failure line. Styles are inline
 * design-system tokens, since the dashboard loads none of the popover stylesheets.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Cancel01Icon, PlayIcon } from '@hugeicons/core-free-icons';
import { RECORD_COPY } from '../constants/captureCopy.js';
import { FOUNDATIONS } from '../designFoundations.js';
import { PALETTE } from '../designPalette.js';
import { DESIGN_RECIPES } from '../designRecipes.js';
import { mountVideoThumbnail } from './videoThumbnail.js';
export function useVideoAttachment(load, onEvent) {
    const [status, setStatus] = useState('idle');
    const [url, setUrl] = useState(null);
    const urlRef = useRef(null);
    const mounted = useRef(true);
    const loading = useRef(false);
    const attempt = useRef(0);
    const revoke = () => { if (urlRef.current)
        URL.revokeObjectURL(urlRef.current); urlRef.current = null; };
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; attempt.current++; loading.current = false; revoke(); };
    }, []);
    const fail = useCallback(() => { attempt.current++; loading.current = false; revoke(); setUrl(null); setStatus('failed'); onEvent?.('recording_play_failed'); }, [onEvent]);
    const play = useCallback(async () => {
        if (!mounted.current || loading.current)
            return;
        const current = ++attempt.current;
        loading.current = true;
        setStatus('loading');
        try {
            const file = await load();
            if (!mounted.current || current !== attempt.current)
                return;
            const bytes = Uint8Array.from(atob(file.base64), (c) => c.charCodeAt(0));
            revoke();
            const next = URL.createObjectURL(new Blob([bytes], { type: file.contentType }));
            urlRef.current = next;
            setUrl(next);
            setStatus('playing');
            onEvent?.('recording_played');
        }
        catch (err) {
            if (!mounted.current || current !== attempt.current)
                return;
            console.error('[VideoAttachmentPlayer] Could not load recording', err);
            fail();
        }
        finally {
            if (current === attempt.current)
                loading.current = false;
        }
    }, [load, fail, onEvent]);
    const close = useCallback(() => { attempt.current++; loading.current = false; revoke(); setUrl(null); setStatus('idle'); }, []);
    return { status, url, play, close, fail };
}
const playStyle = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: FOUNDATIONS.space[1], padding: 0, border: 0, background: 'none', color: 'inherit', font: 'inherit', cursor: 'pointer' };
/** The real recording's still with Play over it, including before the file has been saved. */
export function VideoThumbnailButton({ filename, load, onClick, disabled }) {
    const ref = useRef(null);
    const latest = useRef(load);
    latest.current = load;
    useEffect(() => ref.current ? mountVideoThumbnail(ref.current, () => latest.current()) : undefined, [filename]);
    return _jsx("button", { ref: ref, type: "button", "aria-label": RECORD_COPY.play(filename), disabled: disabled, onClick: onClick });
}
/** The trigger; `filename` gives it its accessible name (RECORD_COPY.play). Children are the visible label, if any. */
export function VideoPlayButton({ filename, onClick, disabled, className, style, children }) {
    return _jsxs("button", { type: "button", className: className, "aria-label": RECORD_COPY.play(filename), disabled: disabled, onClick: onClick, style: { ...playStyle, ...style }, children: [_jsx(HugeiconsIcon, { icon: PlayIcon, size: parseFloat(DESIGN_RECIPES.icon.standard), strokeWidth: 2, color: "currentColor", "aria-hidden": "true" }), children] });
}
const playerStyle = { position: 'relative', display: 'block', width: '100%', margin: `${FOUNDATIONS.space[1]} 0 ${FOUNDATIONS.space[2]}` };
// ds-hand-rolled: 50vh caps the player at half the viewport so the popover keeps its controls on screen (prototype proposed.css).
const videoStyle = { display: 'block', width: '100%', maxHeight: '50vh', borderRadius: FOUNDATIONS.radius.control, background: PALETTE.surface.dark };
const closeStyle = { position: 'absolute', top: FOUNDATIONS.space[1], right: FOUNDATIONS.space[1], display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: DESIGN_RECIPES.icon.prominent, height: DESIGN_RECIPES.icon.prominent, padding: 0, border: 0, borderRadius: FOUNDATIONS.radius.full, background: PALETTE.surface.dark, color: PALETTE.surface.darkText, cursor: 'pointer' };
/**
 * The video while `url` is set; the approved failure line with a Download control once `status` is `failed`;
 * nothing otherwise. `as` chooses the wrapper (an `li` inside a file list, a `div` under a comment).
 */
export function VideoAttachmentPlayer({ status, url, onClose, onError, onFallback, as: Tag = 'div', className, errorClassName }) {
    if (status === 'failed') {
        return _jsx(Tag, { className: className, style: { listStyle: 'none' }, children: _jsxs("p", { role: "alert", className: errorClassName, style: { ...DESIGN_RECIPES.text.caption, margin: 0 }, children: [RECORD_COPY.playbackFailed, ' ', _jsx("button", { type: "button", "data-download": true, onClick: onFallback, style: { ...playStyle, textDecoration: 'underline' }, children: "Download" })] }) });
    }
    if (!url)
        return null;
    return _jsxs(Tag, { className: className, style: { ...playerStyle, listStyle: 'none' }, children: [_jsx("video", { controls: true, autoPlay: true, playsInline: true, src: url, style: videoStyle, onError: onError }), _jsx("button", { type: "button", "aria-label": "Close player", onClick: onClose, style: closeStyle, children: _jsx(HugeiconsIcon, { icon: Cancel01Icon, size: parseFloat(DESIGN_RECIPES.icon.compact), strokeWidth: 2, color: "currentColor", "aria-hidden": "true" }) })] });
}
//# sourceMappingURL=VideoAttachmentPlayer.js.map